// biome-ignore-all lint/style/useNamingConvention: scenarioType enum codes and AST `$`-prefixed keys are external identifiers
/**
 * The optimization-scenario SSOT shared by every grain (string / field /
 * reservoir): {@link SCENARIO_FAMILIES} names each scenario run once — its
 * agent-facing stem, source column prefix, and family kind — and
 * {@link buildScenarioFamilyViews} maps that registry onto one timestamp-aligned
 * rate surface per family for a given grain.
 *
 * Each grain supplies only what differs: its source, the kept context, and the
 * per-kind measure shape (`shapeByKind`) layered on the shared `SCENARIO_BASE`.
 * Resolution is exact (`prefix + suffix`), so the `systemOptimized` family never
 * captures the `systemOptimizedChoke` family's columns and vice versa. Ad-hoc
 * simulation families whose grain carries a `comment` column additionally
 * classify a `scenarioType` enum from it via {@link withScenarioTypeColumn}.
 */

import type { AstInput } from '#core/lib/expr/normalize.ts';
import type { PlanNode } from '#core/lib/expr/plan.ts';
import type { DecodedParams, PlanCtx } from '#core/lib/expr/providers/types.ts';
import { type ColumnType, enumCol, type Schema, schemaFromEntries } from '#core/lib/expr/schema/types.ts';

import { kw } from '../ast.ts';
import { type AlignedSeriesViewSpec, createAlignedSeriesView } from '../createAlignedSeriesView.ts';
import type { VirtualView } from '../createReshapedView.ts';
import type { Column, Shape } from '../shapes/index.ts';

/**
 * Scenario-type classification for the free-text run `comment` on the ad-hoc
 * simulation surfaces: the keyword vocabulary each category is fuzzy-matched
 * against. The `classify` op implicitly contributes the `unknown` fallback for
 * rows whose comment matches no category.
 */
const SCENARIO_TYPE_CATEGORIES = {
  FCT: ['field capacity', 'fct'],
  WC: ['water cut', 'reduction in water handling capacity'],
  Others: ['others'],
} as const;

/** The output enum column added by the classify stage. */
const SCENARIO_TYPE_COLUMN = 'scenarioType';

/** The declared enum domain: the category codes plus the classify `unknown` fallback. */
const SCENARIO_TYPE_MEMBERS = [...Object.keys(SCENARIO_TYPE_CATEGORIES), 'unknown'] as const;

/** The categories map literal as an object-AST node `{:FCT [...] :WC [...] :Others [...]}`. */
const scenarioTypeCategoriesAst: AstInput = {
  $map: Object.entries(SCENARIO_TYPE_CATEGORIES).map(([code, keywords]: [string, readonly string[]]) => ({
    k: code,
    v: [...keywords],
  })),
};

/** The canonical free-text comment column on every ad-hoc result shape. */
const COMMENT_CANONICAL = 'comment';

/** Scenario family kinds — drive each grain's per-family extras + scenarioType classification. */
export type ScenarioKind = 'standard' | 'systemOpt' | 'adHocOpt' | 'adHocSim';

/** One optimization-scenario family: agent-facing stem, source column prefix, human label, kind. */
export type ScenarioFamily = {
  readonly stem: string;
  readonly prefix: string;
  readonly label: string;
  readonly kind: ScenarioKind;
};

/**
 * The nine optimization-scenario families, declared once for every grain. The
 * `systemOptimized` and `systemOptimizedChoke` families are separate entries:
 * exact `prefix + suffix` resolution keeps their columns distinct (e.g. the
 * system-optimized choke setpoint `systemOptimizedChokeBeanSize` vs the choke
 * scenario's `systemOptimizedChokeChokeBeanSize`).
 */
export const SCENARIO_FAMILIES: readonly ScenarioFamily[] = [
  { stem: 'SystemOptimized', prefix: 'systemOptimized', label: 'system-optimized', kind: 'systemOpt' },
  { stem: 'SystemOptimizedChoke', prefix: 'systemOptimizedChoke', label: 'system-optimized choke', kind: 'systemOpt' },
  { stem: 'Technical', prefix: 'technical', label: 'technical-potential', kind: 'standard' },
  { stem: 'Hydraulic', prefix: 'hydraulic', label: 'hydraulic-model', kind: 'standard' },
  { stem: 'Fsopr', prefix: 'fsopr', label: 'FSO production-rate (FSOPR)', kind: 'standard' },
  { stem: 'AdHocOptimization1', prefix: 'adHocOptimization1', label: 'ad-hoc optimization #1', kind: 'adHocOpt' },
  { stem: 'AdHocOptimization2', prefix: 'adHocOptimization2', label: 'ad-hoc optimization #2', kind: 'adHocOpt' },
  { stem: 'AdHocSimulation1', prefix: 'adHocSimulation1', label: 'ad-hoc simulation #1', kind: 'adHocSim' },
  { stem: 'AdHocSimulation2', prefix: 'adHocSimulation2', label: 'ad-hoc simulation #2', kind: 'adHocSim' },
];

/** Per-grain config consumed by {@link buildScenarioFamilyViews}. */
type ScenarioGrainConfig = {
  /** Prepended to each family stem to form the view name (`''` / `Field` / `Reservoir`). */
  readonly grainStem: string;
  /** The grain's source CDF view (e.g. `NetworkOutputs_Field_Agent`). */
  readonly source: AlignedSeriesViewSpec['source'];
  /** Context columns broadcast onto every aligned row (identity + grain relation). */
  readonly keep: Shape;
  /** Optional hierarchy enrichment (the string grain enriches well/field/reservoir ancestry). */
  readonly enrich?: AlignedSeriesViewSpec['enrich'];
  /** The de-prefixed measure shape per family kind, layered on `SCENARIO_BASE`. */
  readonly shapeByKind: Record<ScenarioKind, Shape>;
  /** Builds the agent-facing description for a family + its resolved view name. */
  readonly describe: (family: ScenarioFamily, name: string) => string;
  /** Client used for the source read + datapoints retrieve. */
  readonly client: AlignedSeriesViewSpec['client'];
  /** Series minter (defaulted by the aligned factory when omitted). */
  readonly mintSeries?: AlignedSeriesViewSpec['mintSeries'];
};

/**
 * Post-compose a built aligned-series view with the scenario-type classifier:
 * declare the `scenarioType` enum column on the view's schema + describe surface,
 * and append a trailing `classify` stage over the per-measure `comment` column.
 *
 * The classify is prepended to the aligned plan's residual ops so it runs on the
 * materialised aligned surface (where `comment` is a per-row value column) before
 * any agent-supplied filter/projection; a filter over the derived `scenarioType`
 * then replays as a residual against the classified rows.
 */
export const withScenarioTypeColumn = (view: VirtualView): VirtualView => {
  const scenarioTypeType: ColumnType = enumCol(SCENARIO_TYPE_MEMBERS);
  const basePlan = view.provider.plan;
  if (basePlan === undefined) {
    throw new Error(`scenario-type classify requires an aligned (plan) provider on ${view.name}`);
  }
  const withScenarioType = (schema: Schema): Schema =>
    schemaFromEntries([...schema, [SCENARIO_TYPE_COLUMN, scenarioTypeType] as const]);
  const classifyNode = (schema: Schema): PlanNode => ({
    op: 'classify',
    args: {
      name: 'classify',
      params: [kw(COMMENT_CANONICAL), scenarioTypeCategoriesAst],
      into: SCENARIO_TYPE_COLUMN,
    },
    schemaAfter: withScenarioType(schema),
  });
  return {
    name: view.name,
    provider: {
      ...view.provider,
      schema: (name: string, params) => withScenarioType(view.provider.schema(name, params)),
      describe: (name: string) => {
        const doc = view.provider.describe?.(name);
        if (doc === undefined) {
          return undefined;
        }
        return {
          ...doc,
          columns: [
            ...doc.columns,
            {
              name: SCENARIO_TYPE_COLUMN,
              type: scenarioTypeType,
              description:
                'Scenario type classified from the free-text run comment (FCT, WC, Others, or unknown). Group on it directly — never re-classify the comment.',
            },
          ],
        };
      },
      plan: (name: string, params: DecodedParams, ctx: PlanCtx) => {
        const planned = basePlan(name, params, ctx);
        return {
          source: planned.source,
          residual: [classifyNode(planned.schema), ...planned.residual],
          schema: withScenarioType(planned.schema),
        };
      },
    },
  };
};

/**
 * Build one timestamp-aligned, wide-typed rate surface per scenario family for a
 * grain: name = `${grainStem}${stem}Rates`, measures = the kind's
 * `shapeByKind` shape resolved at the family prefix, plus the aligned
 * `timestamp` and the kept context. Ad-hoc simulation families whose shape
 * carries a `comment` column are wrapped with the scenarioType classifier.
 */
export const buildScenarioFamilyViews = (config: ScenarioGrainConfig): readonly VirtualView[] =>
  SCENARIO_FAMILIES.map((family: ScenarioFamily): VirtualView => {
    const name = `${config.grainStem}${family.stem}Rates`;
    const shape = config.shapeByKind[family.kind];
    const view = createAlignedSeriesView({
      name,
      description: config.describe(family, name),
      source: config.source,
      keep: config.keep,
      groups: { shape, prefix: family.prefix },
      enrich: config.enrich,
      client: config.client,
      mintSeries: config.mintSeries,
    });
    const classify = family.kind === 'adHocSim' && shape.some((c: Column) => c.canonical === COMMENT_CANONICAL);
    return classify ? withScenarioTypeColumn(view) : view;
  });
